import React from 'react';
import { Link } from 'react-router-dom';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { useLogo } from '../hooks/useLogo';
import { useHasPublicCondominiums } from '../hooks/useHasPublicCondominiums';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import { trackWhatsAppClick } from '../services/analyticsService';

/**
 * Rodapé padrão do site — 5 colunas: marca, links, contato, legal e redes sociais.
 */
function Footer() {
  const {
    companyName,
    companyDescription,
    contactAddress,
    fullAddress,
    mapsLink,
    contactEmail,
    contactPhone,
    contactWhatsapp,
    socialFacebook,
    socialInstagram,
    socialLinkedin,
    socialTwitter,
    footerBackgroundColor
  } = useCompanySettings();

  const { logo: footerLogo } = useLogo('footer');
  const { openPreferences, isInternalRoute } = useCookieConsent();
  const hasCondominiums = useHasPublicCondominiums();

  const imageStyles = {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    pointerEvents: 'none'
  };

  const year = new Date().getFullYear();

  return (
    <footer
      className="font-montserrat text-white py-12"
      style={{ backgroundColor: footerBackgroundColor || '#111827' }}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          <div>
            {footerLogo && (
            <img
              src={footerLogo}
              alt="Logo"
              className="h-24 mb-4"
              style={imageStyles}
              draggable={false}
            />
            )}
            <p className="text-gray-400">
              {companyDescription || 'Sua plataforma completa para encontrar o imóvel dos seus sonhos.'}
            </p>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Links Rápidos</h4>
            <nav aria-label="Rodapé">
              <ul className="space-y-2">
                <li>
                  <Link to="/propriedades" className="text-gray-400 hover:text-white transition-colors">
                    Propriedades
                  </Link>
                </li>
                {hasCondominiums && (
                  <li>
                    <Link to="/condominios" className="text-gray-400 hover:text-white transition-colors">
                      Condomínios
                    </Link>
                  </li>
                )}
                <li>
                  <Link to="/favoritos" className="text-gray-400 hover:text-white transition-colors">
                    Favoritos
                  </Link>
                </li>
                <li>
                  <Link to="/comparacao" className="text-gray-400 hover:text-white transition-colors">
                    Comparação
                  </Link>
                </li>
                <li>
                  <Link to="/contato" className="text-gray-400 hover:text-white transition-colors">
                    Contato
                  </Link>
                </li>
                <li>
                  <Link to="/sobre" className="text-gray-400 hover:text-white transition-colors">
                    Sobre nós
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-gray-400 hover:text-white transition-colors">
                    Blog
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Contato</h4>
            <ul className="space-y-2 text-gray-400">
              {contactEmail && (
                <li className="flex items-center gap-2">
                  <FaEnvelope className="w-4 h-4 flex-shrink-0" />
                  <a href={`mailto:${contactEmail}`} className="hover:text-white transition-colors">
                    {contactEmail}
                  </a>
                </li>
              )}
              {contactPhone && (
                <li className="flex items-center gap-2">
                  <FaPhone className="w-4 h-4 flex-shrink-0" />
                  <a href={`tel:${contactPhone}`} className="hover:text-white transition-colors">
                    {contactPhone}
                  </a>
                </li>
              )}
              {contactWhatsapp && (
                <li className="flex items-center gap-2">
                  <FaWhatsapp className="w-4 h-4 flex-shrink-0" />
                  <a
                    href={`https://wa.me/${contactWhatsapp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                    onClick={() => trackWhatsAppClick({ source: 'footer' })}
                  >
                    {contactWhatsapp}
                  </a>
                </li>
              )}
              {(fullAddress || contactAddress) && (
                <li className="flex items-start gap-2">
                  <FaMapMarkerAlt className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {mapsLink ? (
                    <a
                      href={mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm leading-relaxed hover:text-white transition-colors cursor-pointer"
                      title="Abrir no Google Maps"
                    >
                      {fullAddress || contactAddress}
                    </a>
                  ) : (
                    <span className="text-sm leading-relaxed">{fullAddress || contactAddress}</span>
                  )}
                </li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Legal</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link to="/politica-de-privacidade" className="hover:text-white transition-colors">
                  Política de Privacidade
                </Link>
              </li>
              <li>
                <Link to="/termos-de-uso" className="hover:text-white transition-colors">
                  Termos de Uso
                </Link>
              </li>
              {!isInternalRoute && (
                <li>
                  <button
                    type="button"
                    onClick={openPreferences}
                    className="text-left hover:text-white transition-colors"
                  >
                    Preferências de cookies
                  </button>
                </li>
              )}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-lg mb-4">Redes Sociais</h4>
            <div className="flex gap-4 flex-wrap">
              {socialFacebook && (
                <a
                  href={socialFacebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#1877F2] transition-colors"
                  title="Facebook"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
              )}
              {socialInstagram && (
                <a
                  href={socialInstagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#E4405F] transition-colors"
                  title="Instagram"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
              )}
              {socialLinkedin && (
                <a
                  href={socialLinkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#0A66C2] transition-colors"
                  title="LinkedIn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              )}
              {socialTwitter && (
                <a
                  href={socialTwitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#1DA1F2] transition-colors"
                  title="X (Twitter)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>
            © {year} {companyName || 'Imobiliária'}. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
